/**
 * Helper Components - Optimized Barrel Export
 * 
 * This file exports many reusable components. For optimal bundle size:
 * 
 * FREQUENTLY USED (import directly - tree-shakeable):
 * - CustomInput, CustomSelect, CustomTextArea, CustomHTMLSelect, CustomCheckBox
 * - selectTypes (constant)
 * 
 * HEAVY COMPONENTS (consider dynamic imports for better code splitting):
 * - CustomReactPlayer (includes video player dependencies)
 * - ProfilePictureButtonComponent (includes FilePond ~200KB)
 * - DocumentUploadComponent (includes FilePond)
 * - CustomAsyncPaginate, CustomAsyncCreatablePaginate (includes react-select async)
 * 
 * LESS FREQUENTLY USED (can be dynamically imported):
 * - RegisterModal, ConfirmationModal
 * - PhoneOTPComponent (includes ReCAPTCHA)
 * - TimeRangePicker
 * 
 * For dynamic imports, use:
 *   import { lazy, Suspense } from 'react'
 *   const CustomReactPlayer = lazy(() => import('src/consumer/components/helperComponents').then(m => ({ default: m.CustomReactPlayer })))
 * 
 * Or use the pre-configured dynamic imports from:
 *   import { CustomReactPlayer } from 'src/consumer/components/helperComponents/dynamic'
 */

import { ChevronRightIcon } from '@heroicons/react/solid'
import BrokenImage from '@mui/icons-material/BrokenImage'
import CameraAlt from '@mui/icons-material/CameraAlt'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Close from '@mui/icons-material/Close'
import Download from '@mui/icons-material/Download'
import Edit from '@mui/icons-material/Edit'
import Pause from '@mui/icons-material/Pause'
import PlayArrow from '@mui/icons-material/PlayArrow'
import Upload from '@mui/icons-material/Upload'

import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { IndeterminateLoading } from 'src/consumer/components/ConsumerProfilePage/ProfileLeftPannelStyle'
import { apiURL, mediaURL } from 'src/consumer/services'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import {
  AvatarActionIcon,
  MentorProfileButton,
  MentorProfileButtonContainer,
  StyledMentorPhoneNumberInput,
} from 'src/mentor/components/MentorProfilePage/MentorProfilePageStyles'
import { getImageLink, sleep } from 'src/mentor/helpers/utilityFunctions'
import { InputBox, InputBoxContainer, InputBoxInsideContainer, NextIcon } from './Form/FormStyles'
import {
  AnimatedHamburgerIconContainer,
  AnimatedHamburgerIconHorizontalLine,
  AutocompleteHelperStyledButton,
  CircularProgressDocument,
  ConfirmationModalDialogPaper,
  ConfirmationModalPrimaryButton,
  ConfirmationModalSecondaryButton,
  CustomDocumentUploadButton,
  CustomPlayerContainer,
  CustomPlayerControlsContainer,
  CustomPlayerSliderContainer,
  DocumentUploadActionsContainer,
  DownloadDocumentButton,
  EditDocumentButton,
  GenericLoadingOverlayBase,
  InputComponentContainer,
  RegisterButton,
  RegisterModalActions,
  RegisterModalContent,
  RegisterModalTitle,
  SectionGridItem,
  StyledAsyncCreatableSelect,
  StyledAsyncSelect,
  StyledCreatableSelect,
  StyledCustomCheckBox,
  StyledCustomHTMLOptions,
  StyledCustomHTMLSelect,
  StyledCustomInput,
  StyledCustomInputContainer,
  StyledCustomReactPlayer,
  StyledCustomReactPlayerSlider,
  StyledCustomSelect,
  StyledCustomTextArea,
  StyledLabel,
  StyledSubLabel,
  ViewDocumentButton
} from './helperComponentsStyles'

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginGetFile from 'filepond-plugin-get-file'
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css'
import FilePondPluginImageEdit from 'filepond-plugin-image-edit'
import 'filepond-plugin-image-edit/dist/filepond-plugin-image-edit.css'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import 'filepond/dist/filepond.min.css'
import { FilePond, registerPlugin } from 'react-filepond'
import ReCAPTCHA from 'react-google-recaptcha'
// import 'react-phone-input-2/lib/bootstrap.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { StyledPhoneInputContainer } from 'src/clinic/views/onboardng/ClinicOnboardingStyles'
// Doka CSS is now loaded dynamically via dokaLoader
import { StyledMentorJoinVerifyLink } from 'src/mentor/components/MentorPublicPage/MentorPublicPageStyledComponents'
// import 'react-phone-input-2/lib/bootstrap.css'
import screenfull from 'screenfull'
import { getDataWithParams } from '../services/profileService'
import './helperComponentsCss.css'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { Spinner } from 'react-bootstrap'
import CreatableSelect from 'react-select/creatable'
import subtitleFile from 'src/assets/subtitles/subtitles_en.vtt'
import { calculateMinWidthAndHeight } from 'src/consumer/helpers/utilFunctions'

dayjs.extend(customParseFormat)
// get default properties

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginImageEdit,
  FilePondPluginGetFile,
  FilePondPluginFileValidateType,
  FilePondPluginImageTransform,
  FilePondPluginImageValidateSize,
)

export function RegisterModal({ onClickSignIn, onClickRegister, titleText, contentText, ...rest }) {
  return (
    <Dialog
      {...rest}
      PaperProps={{
        sx: { padding: '20px 10px', borderTop: '0.25rem solid #8247f5' },
      }}
    >
      <RegisterModalTitle>
        {titleText || 'Please register or sign in to continue.'}
      </RegisterModalTitle>
      <RegisterModalContent>
        {contentText || 'You need to register or sign in to access the features of AUMHUM.'}
      </RegisterModalContent>
      <RegisterModalActions>
        {onClickRegister && <RegisterButton onClick={onClickRegister}>Register</RegisterButton>}
        {onClickSignIn && <RegisterButton onClick={onClickSignIn}>Sign In</RegisterButton>}
      </RegisterModalActions>
    </Dialog>
  )
}

export function OnboardingInput({ rightArrowFunction, label, ...rest }) {
  return (
    <>
      {label && <StyledLabel>{label}</StyledLabel>}
      <InputBoxContainer>
        <InputBoxInsideContainer>
          <InputBox {...rest} />
          {rightArrowFunction && (
            <NextIcon disabled={!rest?.value} onClick={rightArrowFunction}>
              <ChevronRightIcon sx={{ color: '#fff' }} />
            </NextIcon>
          )}
        </InputBoxInsideContainer>
      </InputBoxContainer>
    </>
  )
}

export function CustomInput({
  label,
  subLabel,
  invalid,
  invalidMessage,
  inputRef,
  valid = false,
  component,
  labelPlacement,
  componentContainer,
  inputAndMessageContainer,
  pureInputProps = {},
  inputAnsMessageContainerStyles = {},
  conponentContainerStyles: componentContainerStyles = {},
  fadedOutPlaceholder,
  showClearInputButton = false,
  checkingValidity,
  endIcon,
  preFixText,
  value,
  ...rest
}) {
  // Ensure value is always defined to prevent uncontrolled/controlled warning
  const controlledValue = value != null ? value : ''
  const [inputFocused, setInputFocused] = useState(false)

  const CustomStyledInputComponentContainer = useMemo(() => {
    return componentContainer ? componentContainer : InputComponentContainer
  }, [componentContainer])

  const CustomStyledInputAndMessageContainer = useMemo(() => {
    return inputAndMessageContainer ? inputAndMessageContainer : StyledCustomInputContainer
  }, [inputAndMessageContainer])
  const CustomStyledInputComponent = useMemo(() => {
    return component ? component : StyledCustomInput
  }, [component])

  const endIconStylesForInputAnsMsg = endIcon
    ? { paddingTop: '0px', paddingBottom: '0px', paddingRight: '0px' }
    : {}
  const endIconStylesForInputComponent = endIcon
    ? { marginTop: '10px', marginBottom: '10px', marginRight: '10px' }
    : {}

  return (
    <CustomStyledInputComponentContainer
      labelPlacement={labelPlacement}
      style={componentContainerStyles}
    >
      {label && (
        <StyledLabel htmlFor={rest?.id}>
          {label} {rest?.required && '*'}
        </StyledLabel>
      )}
      {subLabel && <StyledSubLabel htmlFor={rest?.id}>{subLabel}</StyledSubLabel>}
      <CustomStyledInputAndMessageContainer
        invalid={invalid}
        style={{ ...inputAnsMessageContainerStyles, ...endIconStylesForInputAnsMsg }}
        inputFocused={inputFocused}
      >
        {preFixText && <div className="pre-fix-text">{preFixText}</div>}
        <CustomStyledInputComponent
          {...rest}
          ref={inputRef}
          invalid={invalid}
          style={{ ...(rest?.style || {}), ...(endIconStylesForInputComponent || {}) }}
          fadedOutPlaceholder={fadedOutPlaceholder}
          onFocus={() => {
            setInputFocused(true)
          }}
          onBlur={() => {
            setInputFocused(false)
          }}
          {...pureInputProps}
          // Ensure HTML validation attributes are preserved
          min={pureInputProps?.min ?? rest?.min}
          max={pureInputProps?.max ?? rest?.max}
          step={pureInputProps?.step ?? rest?.step}
          // Set value last to ensure it's always controlled and overrides any value in rest or pureInputProps
          value={controlledValue}
        />
        {showClearInputButton && !!rest?.value && (
          <IconButton
            size="small"
            onClick={() => rest?.onChange({ target: { value: '', id: rest?.id } })}
          >
            <Close sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
        {checkingValidity && (
          <div style={{ minWidth: '30px' }}>
            <CircularProgress size={30} sx={{ height: '19.5px !important', color: '#4d4d4d' }} />
          </div>
        )}
        {valid && (
          <div style={{ minWidth: '30px' }}>
            <CheckCircle sx={{ color: 'green' }} />
          </div>
        )}
        {invalid && (
          <div style={{ minWidth: '30px' }}>
            <Cancel sx={{ color: 'red' }} />
          </div>
        )}
        {endIcon || <></>}
      </CustomStyledInputAndMessageContainer>
      {invalid && invalidMessage && <div>{invalidMessage}</div>}
    </CustomStyledInputComponentContainer>
  )
}

export function CustomTextArea({
  label,
  subLabel,
  invalidMessage,
  customTextAreaComponent,
  pureInputProps,
  textAreaRef,
  ...rest
}) {
  const TextAreaComponent = useMemo(() => {
    return customTextAreaComponent ? customTextAreaComponent : StyledCustomTextArea
  }, [customTextAreaComponent])

  return (
    <div className="d-flex flex-column justify-content-center align-items-start gap-2">
      {label && (
        <StyledLabel>
          {label} {rest?.required && '*'}
        </StyledLabel>
      )}
      {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
      <TextAreaComponent {...rest} {...pureInputProps} ref={textAreaRef} />
      {rest?.invalid && <div>{invalidMessage}</div>}
    </div>
  )
}

export function CustomCheckBox({ label, makeWholeClickable = true, ...rest }) {
  const styledLabelHtmlFor = useMemo(() => {
    return makeWholeClickable ? rest?.id || '' : null
  }, [makeWholeClickable])

  return (
    <div className="d-flex justify-content-start align-items-center gap-2">
      <StyledCustomCheckBox {...rest} />
      {label && (
        <StyledLabel htmlFor={styledLabelHtmlFor}>
          {label} {rest?.required && '*'}
        </StyledLabel>
      )}
    </div>
  )
}

export function CustomCreatableSelect({
  id = 'id',
  label,
  subLabel,
  options,
  selectType = selectTypes.object,
  onChangeEvent,
  ...rest
}) {
  function handleChange(selectValue) {
    if (onChangeEvent) {
      var valueToPut
      if (selectType === selectTypes?.string) {
        valueToPut = rest.isMulti
          ? selectValue.map((val) => rest.getOptionValue(val))
          : rest.getOptionValue(selectValue)
      } else {
        valueToPut = selectValue
      }
      const event = {
        target: { value: valueToPut, id: id },
      }
      onChangeEvent(event)
    } else {
      rest?.onChange(selectValue)
    }
  }

  function getValue(selectValue) {
    if (selectType === selectTypes?.string) {
      if (rest?.isMulti) {
        return rest?.value?.map((val) => {
          const foundOption = options?.find((option) => rest.getOptionValue(option) === val)
          return foundOption
        })
      } else {
        return options?.find((option) => rest.getOptionValue(option) === rest?.value)
      }
    }
    return selectValue
  }

  return (
    <div className="d-flex flex-column justify-content-start align-items-stretch gap-2">
      <div className="d-flex flex-column justify-content-start align-items-stretch">
        {label && (
          <StyledLabel>
            {label} {rest?.required && '*'}
          </StyledLabel>
        )}
        {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
      </div>
      <StyledCreatableSelect
        {...rest}
        value={getValue(rest?.value)}
        classNamePrefix="creatable-react-select"
        options={options}
        onChange={handleChange}
      />
    </div>
  )
}

export function CustomAsyncPaginate({
  id = 'id',
  label,
  subLabel,
  options,
  selectType = selectTypes.object,
  onChangeEvent,
  ...rest
}) {
  function handleChange(selectValue) {
    if (onChangeEvent) {
      var valueToPut
      if (selectType === selectTypes?.string) {
        valueToPut = rest.isMulti
          ? selectValue.map((val) => rest.getOptionValue(val))
          : rest.getOptionValue(selectValue)
      } else {
        valueToPut = selectValue
      }
      const event = {
        target: { value: valueToPut, id: id },
      }
      onChangeEvent(event)
    } else {
      rest?.onChange(selectValue)
    }
  }

  function getValue(selectValue) {
    if (selectType === selectTypes?.string) {
      if (rest?.isMulti) {
        return rest?.value?.map((val) => {
          const foundOption = options?.find((option) => rest.getOptionValue(option) === val)
          return foundOption
        })
      } else {
        return options?.find((option) => rest.getOptionValue(option) === rest?.value)
      }
    }
    return selectValue
  }

  return (
    <div className="d-flex flex-column justify-content-start align-items-stretch gap-2 __async-paginate-container">
      {(label || subLabel) && (
        <div className="d-flex flex-column justify-content-start align-items-stretch">
          {label && (
            <StyledLabel>
              {label} {rest?.required && '*'}
            </StyledLabel>
          )}
          {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
        </div>
      )}
      <StyledAsyncSelect
        {...rest}
        value={rest?.value}
        classNamePrefix="async-react-select"
        options={options}
        onChange={handleChange}
      />
    </div>
  )
}

export function CustomAsyncCreatablePaginate({
  id = 'id',
  label,
  subLabel,
  options,
  selectType = selectTypes.object,
  onChangeEvent,
  AsyncCreatableSelect = StyledAsyncCreatableSelect,
  ...rest
}) {
  function handleChange(selectValue) {
    if (onChangeEvent) {
      var valueToPut
      if (selectType === selectTypes?.string) {
        valueToPut = rest.isMulti
          ? selectValue.map((val) => rest.getOptionValue(val))
          : rest.getOptionValue(selectValue)
      } else {
        valueToPut = selectValue
      }
      const event = {
        target: { value: valueToPut, id: id },
      }
      onChangeEvent(event)
    } else {
      rest?.onChange(selectValue)
    }
  }

  function getValue(selectValue) {
    if (selectType === selectTypes?.string) {
      if (rest?.isMulti) {
        return rest?.value?.map((val) => {
          const foundOption = options?.find((option) => rest.getOptionValue(option) === val)
          return foundOption
        })
      } else {
        return options?.find((option) => rest.getOptionValue(option) === rest?.value)
      }
    }
    return selectValue
  }

  return (
    <div className="d-flex flex-column justify-content-start align-items-stretch gap-2 __async-paginate-container">
      <div className="d-flex flex-column justify-content-start align-items-stretch">
        {label && (
          <StyledLabel>
            {label} {rest?.required && '*'}
          </StyledLabel>
        )}
        {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
      </div>
      <AsyncCreatableSelect
        {...rest}
        value={rest?.value}
        classNamePrefix="async-creatable-react-select"
        options={options}
        onChange={handleChange}
      />
    </div>
  )
}

export function CustomSelect({
  id = 'id',
  label,
  subLabel,
  options,
  selectType = selectTypes.object,
  onChangeEvent,
  customComponent,
  setFirstByDefault = false,
  value,
  mainContaineStyles = {},
  ...rest
}) {
  const SelectComponent = useMemo(() => {
    return customComponent ? customComponent : StyledCustomSelect
  }, [customComponent])

  function handleChange(selectValue) {
    if (onChangeEvent) {
      var valueToPut
      if (selectType === selectTypes?.string) {
        valueToPut = rest.isMulti
          ? selectValue.map((val) => rest?.getOptionValue(val))
          : rest?.getOptionValue(selectValue)
      } else {
        valueToPut = selectValue
      }
      const event = {
        target: { value: valueToPut, id: id, type: 'react-select' },
      }
      onChangeEvent(event)
    } else {
      rest?.onChange(selectValue)
    }
  }

  function getValue(selectValue) {
    if (selectType === selectTypes?.string) {
      if (rest?.isMulti) {
        if (value) {
          return value?.map((val) => {
            return (
              options?.find((option) => rest?.getOptionValue(option) === val) || {
                value: val,
                id: val,
                title: val,
                name: val,
                label: val,
              }
            )
          })
        }
        return null
      } else {
        if (value)
          return (
            options?.find((option) => rest?.getOptionValue(option) === value) || {
              value: value,
              id: value,
              title: value,
              name: value,
              label: value,
            }
          )
        else {
          return undefined
        }
      }
    }
    return selectValue
  }

  const currentValue = useMemo(() => {
    return getValue(value)
  }, [value, options])

  useEffect(() => {
    if (setFirstByDefault) {
      const value = getValue(value)
      if (rest?.isMulti) {
        if (!value || value.length === 0) {
          handleChange([options[0]])
        }
      } else {
        if (!value) {
          handleChange(options[0])
        }
      }
    }
  }, [setFirstByDefault, value, options])

  return (
    <div
      style={{
        width: '100%',
        ...mainContaineStyles,
      }}
      className="d-flex flex-column justify-content-start align-items-stretch gap-2"
    >
      {(label || subLabel) && (
        <div className="d-flex flex-column justify-content-start align-items-stretch">
          {label && (
            <StyledLabel>
              {label} {rest?.required && '*'}
            </StyledLabel>
          )}
          {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
        </div>
      )}
      <SelectComponent
        {...rest}
        value={currentValue}
        classNamePrefix="react-select"
        options={options}
        onChange={handleChange}
      />
    </div>
  )
}

export function SelectInsidePopout({ ...rest }) {
  const [isOpen, setIsOpen] = useState(false)

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: 240,
      margin: 8,
    }),
    menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
  }

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      target={
        <Button
          iconAfter={<ChevronDown />}
          onClick={() => setIsOpen((prev) => !prev)}
          isSelected={isOpen}
        >
          {rest?.value ? `State: ${rest?.getOptionValue(rest?.value)}` : 'Select a State'}
        </Button>
      }
    >
      <CustomSelect
        {...rest}
        autoFocus
        backspaceRemovesValue={false}
        components={{ DropdownIndicator, IndicatorSeparator: null }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen
        onChange={(newValue) => {
          rest.onChange(newValue)
          setIsOpen(false)
        }}
        styles={selectStyles}
        placeholder="Search..."
        tabSelectsValue={false}
      />
    </Dropdown>
  )
}

export const AUTOMATIC_LIST_ADDING_COMPONENT_DIRECTIONS = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
}

export function AutomaticListAddingComponent({
  id,
  value = [],
  onChange,
  label,
  direction = AUTOMATIC_LIST_ADDING_COMPONENT_DIRECTIONS.VERTICAL,
  maxWidthOfItems = '100%',
  InputComponent = CustomInput,
  getValueFromSinlgeInput = (e) => e.target.value,
  disabled,
  extraInputProps = {},
  inputItemPrefix = '',
}) {
  function changeData(e, idx) {
    var updatedValue = value

    var valueFromE = getValueFromSinlgeInput(e)

    //here working with the new empty value.
    if (idx >= value?.length) {
      // add the new value to the array
      updatedValue = [...(updatedValue || []), valueFromE]
    } else {
      //udpate the index item
      updatedValue = updatedValue?.map((val, updatedValIdx) => {
        return idx === updatedValIdx ? valueFromE : val
      })
    }

    //remove the empty items from array
    updatedValue = updatedValue?.filter((val) => {
      const valueToWorkWith = val?.replace(inputItemPrefix, '')
      return !!valueToWorkWith
    })
    onChange({
      target: {
        id: id,
        value: updatedValue,
      },
    })
  }

  const CLASSES_TO_USE = {
    [AUTOMATIC_LIST_ADDING_COMPONENT_DIRECTIONS.VERTICAL]: 'd-flex flex-column gap-1',
    [AUTOMATIC_LIST_ADDING_COMPONENT_DIRECTIONS.HORIZONTAL]: 'd-flex gap-1 flex-wrap',
  }

  return (
    <div>
      {label && <label>{label}</label>}
      <div
        className={
          CLASSES_TO_USE?.[direction] ||
          CLASSES_TO_USE?.[AUTOMATIC_LIST_ADDING_COMPONENT_DIRECTIONS.VERTICAL]
        }
      >
        {[...(value || []), ''.replace(inputItemPrefix, '')]?.map((val, idx) => (
          <InputComponent
            key={idx}
            value={val}
            showLabel={false}
            containerStyles={{
              maxWidth: maxWidthOfItems,
              width: '100%',
            }}
            disabled={disabled}
            id="val"
            valueId={id}
            onChange={(e) => {
              changeData(e, idx)
            }}
            {...extraInputProps}
          />
        ))}
      </div>
    </div>
  )
}

const Menu = (props) => {
  const shadow = 'hsla(218, 50%, 10%, 0.1)'
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: 4,
        boxShadow: `0 0 0 1px ${shadow}, 0 4px 11px ${shadow}`,
        marginTop: 8,
        position: 'absolute',
        zIndex: 2,
      }}
      {...props}
    />
  )
}
const Blanket = (props) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: 'fixed',
      zIndex: 1,
    }}
    {...props}
  />
)

const Dropdown = ({ children, isOpen, target, onClose }) => (
  <div style={{ position: 'relative' }}>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
)
const Svg = (p) => (
  <svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" {...p} />
)
const DropdownIndicator = () => (
  <div style={{ color: 'green', height: 24, width: 32 }}>
    <Svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Svg>
  </div>
)
const ChevronDown = () => (
  <Svg style={{ marginRight: -6 }}>
    <path
      d="M8.292 10.293a1.009 1.009 0 0 0 0 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 0 0 0-1.419.987.987 0 0 0-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 0 0-1.406 0z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </Svg>
)

export function CustomHTMLSelect({
  label,
  subLabel,
  options,
  selectComponent,
  optionComponent,
  getOptionLabel,
  getOptionValue,
  outerMostDivStyle = {},
  ...rest
}) {
  const HTMLSelectComponent = useMemo(() => {
    return selectComponent ? selectComponent : StyledCustomHTMLSelect
  }, [selectComponent])
  const OptionComponent = useMemo(() => {
    return optionComponent ? optionComponent : StyledCustomHTMLOptions
  }, [optionComponent])

  return (
    <div
      className="d-flex flex-column justify-content-start align-items-stretch gap-2"
      style={outerMostDivStyle}
    >
      {(label || subLabel) && (
        <div className="d-flex flex-column justify-content-start align-items-stretch">
          {label && (
            <StyledLabel>
              {label} {rest?.required && '*'}
            </StyledLabel>
          )}
          {subLabel && <StyledSubLabel>{subLabel}</StyledSubLabel>}
        </div>
      )}
      <HTMLSelectComponent {...rest}>
        {options.map((option, idx) => {
          return (
            <OptionComponent
              {...(option?.props || {})}
              key={idx}
              value={getOptionValue(option) || option?.id}
            >
              {getOptionLabel(option) || option?.name || option?.title}
            </OptionComponent>
          )
        })}
      </HTMLSelectComponent>
    </div>
  )
}

// export function MultiplePictureUploadButton({
//   imageIds,
//   onDeleteImageId,
//   onAddImageId,
//   aspectWidth,
//   aspectHeight,
//   imageUploadUrl,
//   fileUploadApiParams = {},
//   preferCamera = false,
//   disabled = false,
// }) {
//   const [viewerOpen, setViewerOpen] = useState(false)
//   const [selectedImageId, setSelectedImageId] = useState(null)

//   const handleImageClick = (imageId) => {
//     setSelectedImageId(imageId)
//     setViewerOpen(true)
//   }

//   const handleCloseViewer = () => {
//     setViewerOpen(false)
//     setSelectedImageId(null)
//   }

//   return (
//     <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
//       {imageIds?.map((imageId) => {
//         return (
//           <div
//             key={imageId}
//             style={{
//               display: 'flex',
//               justifyContent: 'center',
//               alignItems: 'center',
//               position: 'relative',
//               cursor: 'pointer',
//             }}
//           >
//             <img
//               alt={'imageId'}
//               src={getImageLink(imageId)}
//               onClick={() => handleImageClick(imageId)}
//               style={{
//                 width: '64px',
//                 height: '64px',
//                 borderRadius: '8px',
//                 border: '1px solid #cecece',
//                 objectFit: 'cover',
//                 transition: 'transform 0.2s ease-in-out',
//               }}
//               onMouseEnter={(e) => {
//                 e.target.style.transform = 'scale(1.05)'
//               }}
//               onMouseLeave={(e) => {
//                 e.target.style.transform = 'scale(1)'
//               }}
//             />

//             {/* Delete button */}
//             {!disabled && (
//               <IconButton
//                 onClick={(e) => {
//                   e.stopPropagation() // Prevent opening viewer
//                   onDeleteImageId(imageId)
//                 }}
//                 size="small"
//                 style={{
//                   position: 'absolute',
//                   top: '-8px',
//                   right: '-8px',
//                   backgroundColor: '#fff',
//                   border: '1px solid #e0e0e0',
//                   boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
//                   width: '24px',
//                   height: '24px',
//                 }}
//               >
//                 <DeleteOutlineSharpIcon
//                   style={{
//                     color: '#f44336',
//                     fontSize: '16px',
//                   }}
//                 />
//               </IconButton>
//             )}
//           </div>
//         )
//       })}
//       {!disabled && (
//         <PictureUploadButton
//           onChange={(e) => onAddImageId(e.target.value)}
//           aspectWidth={aspectWidth}
//           aspectHeight={aspectHeight}
//           imageUploadUrl={imageUploadUrl}
//           fileUploadApiParams={fileUploadApiParams}
//           preferCamera={preferCamera}
//         />
//       )}

//       <ImageViewerModal
//         open={viewerOpen}
//         onClose={handleCloseViewer}
//         imageId={selectedImageId}
//         onDelete={onDeleteImageId}
//         disabled={disabled}
//       />
//     </div>
//   )
// }

export function ReCaptchaPhoneInput({ ...rest }) {
  return
}

export const PhoneOTPComponent = ({
  phoneValue,
  setPhoneValue,
  layout = 'grid',
  initialValue,
  setValidity,
  setValidatedPhone,
  userProfileData,
  setValidatedOtp = () => {},
}) => {
  const [profileData, setProfileData] = useState({
    ...userProfileData,
    mobileVerified: userProfileData?.mobileVerified || false,
  })
  const [captchaVerified, setCaptchaVerified] = useState(false)
  const [otpToUse, setOtpToUse] = useState()
  const [otp, setOtp] = useState('')
  const reCAPTCHAKey = '6LfrNj0pAAAAAAHFhLjzhXhLEXQOaKWYVgJosWck' // Replace with your reCAPTCHA site key
  const [phoneValid, setPhoneValid] = useState(true)
  const [verifyClick, setVerifyClick] = useState(false)

  useEffect(() => {
    const valid = isPhoneValid() && captchaVerified && otp?.length === 6
    setValidity(valid)
    if (valid) {
      setValidatedPhone(phoneValue)
      setValidatedOtp(otp)
    }
  }, [phoneValue, captchaVerified, otp])

  const isPhoneValid = () => {
    // Add your logic to validate phone number
    // const valid = phone?.length > 0 // Basic example, replace with actual validation

    // Check if phone starts with "+", if not, add it
    const formattedPhone = phoneValue?.startsWith('+') ? phoneValue : `+${phoneValue}`

    const valid = isValidPhoneNumber(formattedPhone || '')
    if (profileData?.mobileVerified) {
      setPhoneValid(true)
      return true
    }
    setPhoneValid(valid)
    return valid
  }

  const handleCaptchaChange = async (value) => {
    setCaptchaVerified(!!value)
    if (!value) {
      // Reset OTP when reCAPTCHA is reset
      setOtp('')
    } else {
      // Remove country code and "+" sign from phone number
      const phoneNumberWithoutCountryCode = phoneValue.replace(/^\+/, '').substring(2)
      const getOtpResponse = await getDataWithParams('/api/generate/otp', {
        phone: phoneNumberWithoutCountryCode,
      })
      setOtpToUse()
    }
  }

  const handlePhoneChange = (value) => {
    setPhoneValue(value)
    // Reset captcha and OTP if phone number changes
    if (value !== phoneValue) {
      setCaptchaVerified(false)
      setOtp('')
    }
  }

  useEffect(() => {
    setProfileData({
      ...userProfileData,
      mobileVerified: userProfileData?.mobileVerified || false,
    })
    if (initialValue) {
      setPhoneValue(initialValue)
    }
  }, [userProfileData, initialValue])

  return (
    <>
      {layout === 'grid' ? (
        <Grid container spacing={1}>
          <Grid item md={6}>
            <CustomInput
              country={'in'}
              label="Phone"
              inputAndMessageContainer={StyledPhoneInputContainer}
              component={StyledMentorPhoneNumberInput}
              value={phoneValue}
              onChange={handlePhoneChange}
              invalid={profileData?.mobileVerified ? false : !phoneValid || !captchaVerified}
              valid={profileData?.mobileVerified ? true : phoneValid && captchaVerified}
            />
            <div className="d-flex w-100 justify-content-start align-items-center">
              {phoneValid && !verifyClick && !profileData?.mobileVerified && (
                <StyledMentorJoinVerifyLink
                  variant={'text'}
                  onClick={() => {
                    setVerifyClick(true)
                  }}
                >
                  Click here to verify your phone number and continue
                </StyledMentorJoinVerifyLink>
              )}
            </div>
          </Grid>
          <Grid item md={6}>
            {phoneValid && verifyClick && !captchaVerified && (
              <ReCAPTCHA sitekey={reCAPTCHAKey} onChange={handleCaptchaChange} size="normal" />
            )}
          </Grid>
          <Grid item md={6}>
            {phoneValid && captchaVerified && (
              <CustomInput
                type="text"
                value={otp}
                label="Enter OTP"
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                disabled={!captchaVerified}
              />
            )}
          </Grid>
          {/* {captchaVerified && otp.length !== 6 && (
            <p style={{ color: 'red' }}>Please enter a 6-digit OTP</p>
          )} */}
        </Grid>
      ) : (
        <>
          <CustomInput country={'in'} value={phoneValue} onChange={handlePhoneChange} />
          {!phoneValid && <p style={{ color: 'red' }}>Please enter a valid phone number</p>}

          {phoneValid && (
            <ReCAPTCHA sitekey={reCAPTCHAKey} onChange={handleCaptchaChange} size="normal" />
          )}

          {phoneValid && captchaVerified && (
            <CustomInput
              type="text"
              value={otp}
              label="Enter a 6-digit OTP"
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              disabled={!captchaVerified}
            />
          )}
          {/* {captchaVerified &&  && (
            <p style={{ color: 'red' }}>Please enter a 6-digit OTP</p>
          )} */}
        </>
      )}
    </>
  )
}

export function ProfilePictureButtonComponent({ formState, setFormState, handleChange }) {
  const inputRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [dokaEditor, setDokaEditor] = useState(null)
  const dokaEditorRef = useRef(null)
  const dokaLoadingPromiseRef = useRef(null)
  const minDimensions = calculateMinWidthAndHeight(1)
  const value = formState?.profilePicId
  const profilePicId = formState?.profilePicId

  async function ensureDokaEditor() {
    if (dokaEditorRef.current) {
      return dokaEditorRef.current
    }

    if (!dokaLoadingPromiseRef.current) {
      dokaLoadingPromiseRef.current = import('src/consumer/helpers/dokaLoader')
        .then(({ createDokaEditor }) =>
          createDokaEditor({
            cropAspectRatio: [1],
          }),
        )
        .then((editor) => {
          dokaEditorRef.current = editor
          setDokaEditor(editor)
          return editor
        })
        .finally(() => {
          dokaLoadingPromiseRef.current = null
        })
    }

    return dokaLoadingPromiseRef.current
  }

  async function handleClick() {
    try {
      await ensureDokaEditor()
      window.requestAnimationFrame(() => {
        inputRef.current?.browse()
      })
    } catch (error) {
      console.error('Failed to load Doka editor:', error)
      toast.error('Image editor failed to load. Please try again.')
    }
  }

  const [files, setFiles] = useState()

  async function handleImageChange(event) {
    setSaving(true)
    const file = event.target.files[0]
    if (file) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await postDataWithoutParams('/api/media/file/upload', formData)
      if (response?.status === 200) {
        await sleep(1000)
        setFormState((prev) => ({ ...prev, [event.target.id]: response.data.fileId }))
      } else {
        toast.error('Something went wrong')
        return 0
      }
    }
    event.target.value = ''
    setSaving(false)
  }

  function handleOnData(formData) {
    const files = formData.getAll('files')?.[1]
    const toReturnFormData = new FormData()
    toReturnFormData.append('file', files)
    return toReturnFormData
  }

  async function handleOnLoad(resData) {
    await sleep(1000)
    const responseData = JSON.parse(resData)
    setFormState((prev) => ({ ...prev, profilePicId: responseData.fileId }))
  }

  async function processImage(
    fieldName,
    file,
    metadata,
    load,
    error,
    progress,
    abort,
    transfer,
    options,
  ) {
    // fieldName is the name of the input field
    // file is the actual file object to send
    setSaving(true)
    const formData = new FormData()
    formData.append('file', file)
    const response = await postDataWithoutParams('/api/media/file/upload', formData)
    if (response?.status === 200) {
      await sleep(1000)
      setFormState((prev) => ({ ...prev, profilePicId: response.data.fileId }))
    } else {
      toast.error('Something went wrong')
      return 0
    }
    setSaving(false)
  }

  useEffect(() => {
    if (value) {
      setFiles([
        {
          source: value,
          options: {
            type: 'local',
          },
        },
      ])
    } else {
      setFiles([])
    }
  }, [value])

  useEffect(() => {
    return () => {
      if (dokaEditorRef.current) {
        try {
          dokaEditorRef.current.destroy?.()
        } catch (_error) {
          // Editor may already be destroyed during teardown.
        }
        dokaEditorRef.current = null
      }
    }
  }, [])

  return (
    <>
      <IconButton onClick={handleClick} sx={{ p: 0, position: 'relative' }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <AvatarActionIcon imgIsThere={Boolean(profilePicId)}>
              {profilePicId ? <Edit /> : <CameraAlt />}
            </AvatarActionIcon>
          }
        >
          <MentorProfileButtonContainer>
            {profilePicId && (
              <MentorProfileButton src={`${mediaURL}${profilePicId}`}>
                <BrokenImage sx={{ fontSize: { xs: 10, md: 100 } }} />
              </MentorProfileButton>
            )}
            {!formState?.profilePicId && <MentorProfileButton />}
          </MentorProfileButtonContainer>
          {saving && <IndeterminateLoading />}
        </Badge>
      </IconButton>
      <div style={{ display: 'none' }}>
        <FilePond
          ref={inputRef}
          allowImageEdit
          allowImageValidateSize
          imageValidateSizeMinWidth={minDimensions?.minWidth}
          imageValidateSizeMinHeight={minDimensions?.minHeight}
          imageEditInstantEdit
          imagePreviewHeight={200}
          stylePanelLayout="compact circle"
          // imageEditInstantEdit
          instantUpload={true}
          // files={files}
          // onupdatefiles={setFiles}
          allowMultiple={false}
          server={{
            url: apiURL,
            // process: {
            //   url: '/media/file/upload',
            //   method: 'POST',
            //   // withCredentials: false,
            //   headers: {
            //     Authorization: `Bearer ${getToken()}`,
            //     ...commonHeadersWeb,
            //     'ef-user-country-code': localStorage.getItem('countryCode'),
            //     'ef-app-language': sessionStorage.getItem('languageCode'),
            //   },
            //   //   timeout: 7000,
            //   onload: handleOnLoad,
            //   //   onerror: null,
            //   ondata: handleOnData,
            // },
            process: processImage,
            // load: async (source, load, error, progress, abort, headers) => {
            //   // const headers = new Headers()
            //   // headers.append('Content-Type', '*/*')
            //   // headers.append('Authorization', 'Bearer YourAuthTokenHere')
            //   fetch(`${mediaURL}${source}`, {})
            //     .then(function (response) {
            //       console.log(response)
            //       if (!response.ok) {
            //         // Handle HTTP error
            //         throw new Error(`HTTP error! Status: ${response.status}`)
            //       }
            //       response
            //         .blob()
            //         .then(function (myBlob) {
            //           load(myBlob)
            //         })
            //         .catch(function (blobError) {
            //           error(blobError)
            //         })
            //     })
            //     .catch(function (fetchError) {
            //       error(fetchError)
            //     })
            //   // await axios
            //   //   .get(`${mediaURL}${source}`, {
            //   //     headers: { ...headers, Accept: '*/*', cache: 'cache' },
            //   //     withCredentials: true,
            //   //   })
            //   //   .then(function (response) {
            //   //     console.log(response)
            //   //     if (response.status !== 200) {
            //   //       // Handle HTTP error
            //   //       throw new Error(`HTTP error! Status: ${response.status}`)
            //   //     }
            //   //     return response.data
            //   //   })
            //   //   .then(function (data) {
            //   //     // Assuming 'load' and 'error' functions are defined elsewhere
            //   //     load(data)
            //   //   })
            //   //   .catch(function (error) {
            //   //     console.error(error)
            //   //     // Handle errors here
            //   //   })
            // },
          }}
          acceptedFileTypes={['image/*']}
          name="files"
          allowImageTransform
          styleItemPanelAspectRatio={1}
          // styleButtonProcessItemPosition="bottom"
          stylePanelAspectRatio={1}
          labelIdle={'Drop Your Photo here or <span class="filepond--label-action">Browse</span>'}
          imageEditEditor={dokaEditor}
        />
      </div>
      {/* <input
        id="profilePicId"
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleImageChange}
        accept="image/*"
      /> */}
    </>
  )
}

/**
 * Phase 14j — best-effort image compression before upload. Reduces a multi-MB camera shot to
 * sub-MB JPEG (~85% quality, max 2000px on the longer side) without sacrificing legibility for
 * a credential-doc / certificate scan. Pass-through for PDFs and any non-image — they go to the
 * server as-is. Falls through with the original file on any browser oddity so we never block
 * an upload just because compression failed.
 */
const DEFAULT_DOC_IMAGE_MAX_DIMENSION = 2000
const DEFAULT_DOC_IMAGE_QUALITY = 0.85

async function compressImageFile(file, {
  maxDimension = DEFAULT_DOC_IMAGE_MAX_DIMENSION,
  quality = DEFAULT_DOC_IMAGE_QUALITY,
} = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file
  // Animated GIFs lose their animation if we redraw to a single canvas frame; keep them as-is.
  if (file.type === 'image/gif') return file
  if (typeof document === 'undefined' || typeof URL === 'undefined') return file
  try {
    const objectUrl = URL.createObjectURL(file)
    const img = await new Promise((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = reject
      el.src = objectUrl
    })
    const longSide = Math.max(img.naturalWidth, img.naturalHeight)
    const scale = longSide > maxDimension ? maxDimension / longSide : 1
    const targetW = Math.round(img.naturalWidth * scale)
    const targetH = Math.round(img.naturalHeight * scale)
    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, targetW, targetH)
    URL.revokeObjectURL(objectUrl)
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    )
    if (!blob || blob.size >= file.size) {
      // The compressed blob would be larger (already-tiny PNG / re-encoded JPEG) — keep the original.
      return file
    }
    const ext = (file.name || '').replace(/\.[^.]+$/, '') || 'image'
    return new File([blob], `${ext}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
  } catch (_e) {
    return file
  }
}

// Default file-type whitelist — server accepts arbitrary mime types but Phase 14j surfaces this
// list in the UI so the agent / applicant knows what to upload. Driven by what every existing
// caller already passes via the fileType prop ("application/pdf,image/*").
const DOC_DEFAULT_FILE_TYPE = 'application/pdf,image/*'

function describeAcceptedDocTypes(fileType) {
  const accept = (fileType || DOC_DEFAULT_FILE_TYPE).toLowerCase()
  const friendly = []
  if (accept.includes('application/pdf')) friendly.push('PDF')
  if (accept.includes('image/*') || accept.includes('image/')) friendly.push('JPG / PNG / WebP')
  if (accept.includes('image/heic') || accept.includes('image/heif')) friendly.push('HEIC / HEIF')
  if (!friendly.length) friendly.push('any file')
  return friendly.join(' · ')
}

export function DocumentUploadComponent({
  value,
  onChange,
  handleView,
  handleEdit,
  showDownloadButton,
  fileType,
  customJustifyContent,
  ...rest
}) {
  const inputRef = useRef(null)
  const [saving, setSaving] = useState(false)
  const [fileName, setFileName] = useState('')
  // Track byte sizes so we can show "Compressed 4.1MB → 0.7MB" — useful feedback for an agent
  // who's about to upload a phone-camera scan and has no idea whether it'll fit.
  const [sizeReport, setSizeReport] = useState(null)

  function handleClick() {
    inputRef.current.click()
  }

  async function handleChangeDocument(event) {
    setSaving(true)
    const original = event.target.files[0]
    if (original) {
      const compressed = await compressImageFile(original)
      const file = compressed || original
      const formData = new FormData()
      formData.append('file', file)
      const response = await postDataWithoutParams('/api/media/file/upload', formData)
      if (response?.status === 200) {
        await sleep(1000)
        onChange({
          ...event,
          target: { value: response.data.fileId, id: event.target.id },
        })
        setFileName(response.data.fileName)
        if (compressed && compressed.size < original.size) {
          setSizeReport({
            originalKb: Math.round(original.size / 1024),
            uploadedKb: Math.round(compressed.size / 1024),
          })
        } else {
          setSizeReport({
            originalKb: Math.round(original.size / 1024),
            uploadedKb: Math.round(original.size / 1024),
          })
        }
      } else {
        toast.error('Something went wrong')
        return 0
      }
    }
    event.target.value = ''
    setSaving(false)
  }

  function handleDownload() {}

  // Default View button: open the uploaded asset in a new tab. Caller can override via
  // handleView for special-case viewers (e.g. a PDF modal). Without an override, "View" gives
  // a no-fuss preview the agent can use to confirm the right document went up.
  const defaultHandleView =
    !handleView && value
      ? () => {
          if (typeof window !== 'undefined') {
            window.open(`${mediaURL}${value}`, '_blank', 'noopener,noreferrer')
          }
        }
      : null
  const effectiveHandleView = handleView || defaultHandleView

  const acceptedTypesLabel = describeAcceptedDocTypes(fileType)

  return (
    <>
      {value ? (
        <DocumentUploadActionsContainer
          justifyContent={customJustifyContent ? customJustifyContent : 'center'}
        >
          {!!fileName && <div>Uploaded File: {fileName}</div>}
          <div className="d-flex justify-content-center align-items-center gap-3">
            {/* Phase 14j — View button. Defaults to opening the uploaded asset in a new tab so
                the agent / applicant can confirm the right document made it up; callers that
                want a custom viewer can still pass handleView. */}
            {effectiveHandleView && (
              <ViewDocumentButton disabled={saving} onClick={effectiveHandleView}>
                View
              </ViewDocumentButton>
            )}
            <EditDocumentButton disabled={saving} onClick={handleEdit ? handleEdit : handleClick}>
              <span>Edit</span>
              <Edit />
            </EditDocumentButton>
            {showDownloadButton && (
              <a
                style={{ textDecoration: 'none' }}
                href={`${mediaURL}${value}`}
                rel="noreferrer"
                target="_blank"
              >
                <DownloadDocumentButton disabled={saving} onClick={handleDownload}>
                  <span>Download</span>
                  <Download />
                </DownloadDocumentButton>
              </a>
            )}
          </div>
          {saving && <CircularProgressDocument size={30} />}
          {sizeReport && (
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: 4 }}>
              {sizeReport.originalKb !== sizeReport.uploadedKb
                ? `Compressed ${sizeReport.originalKb}KB → ${sizeReport.uploadedKb}KB before upload.`
                : `Uploaded ${sizeReport.uploadedKb}KB.`}
            </div>
          )}
        </DocumentUploadActionsContainer>
      ) : (
        <>
          <DocumentUploadActionsContainer
            justifyContent={customJustifyContent ? customJustifyContent : 'center'}
          >
            {!!fileName && <div>{fileName}</div>}
            <CustomDocumentUploadButton disabled={saving} onClick={handleClick}>
              <span>Upload</span>
              <Upload />
            </CustomDocumentUploadButton>
            {saving && <CircularProgressDocument size={30} />}
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: 4 }}>
              Accepted: {acceptedTypesLabel}. Images are auto-compressed before upload.
            </div>
          </DocumentUploadActionsContainer>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleChangeDocument}
        accept={fileType || DOC_DEFAULT_FILE_TYPE}
        {...rest}
      />
    </>
  )
}

export function ImageViewComponent({ imageId, ...rest }) {
  return (
    <Avatar sx={{ width: '100%', height: '100%', ...rest?.sx }} {...rest}>
      <BrokenImage />
    </Avatar>
  )
}

export const selectTypes = {
  object: 'object',
  string: 'string',
}

export function OverflowingArrayChips({ allItems, getTitle, maxChips }) {
  const allItemTitles = allItems?.map((item) => {
    return getTitle(item)
  })
  const titleToShowOutside = allItemTitles?.length > 3 ? allItemTitles?.slice(0, 3) : allItemTitles

  return (
    <>
      {allItemTitles?.length === 0 || !allItems ? (
        <div></div>
      ) : (
        <Tooltip title={allItemTitles?.join(', ')}>
          <div className="d-flex flex-wrap gap-2">
            {titleToShowOutside?.map((iTitle, idx) => {
              return <Chip label={iTitle} key={iTitle + idx} />
            })}
            {allItemTitles?.length > maxChips ? `+${allItemTitles?.length - maxChips}` : ''}
          </div>
        </Tooltip>
      )}
    </>
  )
}

export function DescriptionComponent({ description, maxLines }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showMoreButton, setShowMoreButton] = useState(false)
  const descriptionRef = useRef(null)

  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight, 10)
      const totalHeight = descriptionRef.current.scrollHeight

      setShowMoreButton(totalHeight > maxLines * lineHeight)
    }
  }, [description, maxLines])

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const truncatedStyle = {
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  useEffect(() => {
    if (descriptionRef.current) {
      // TODO: In the future, maybe make it responsive (if you change content to automatically decide whenever it should show content or not, that can be done by adding event listener on height change on main div -> divRef, and do the check bellow but inside eventListener)
      // Check the height, to know if you need to display showMoreButton or not.
      if (maxLines > descriptionRef.current.offsetHeight) {
        setShowMoreButton('hide')
      } else {
        setShowMoreButton(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <span ref={descriptionRef} style={isExpanded ? {} : truncatedStyle}>
        {description}
      </span>
      {showMoreButton && <button onClick={toggleExpand}>{isExpanded ? 'Less' : 'More'}</button>}
    </div>
  )
}

export default function ConfirmationModal({
  newImplementation,
  visibility,
  visibilitySetter,
  action,
  body,
  buttonColor,
  onSubmitFunctions,
}) {
  function handleVisibilitySetter(value) {
    if (newImplementation) {
      visibilitySetter((prev) => ({ ...prev, visibility: false }))
    } else {
      visibilitySetter(value)
    }
  }

  async function handleSubmit() {
    handleVisibilitySetter(false)
    await Promise.all(
      onSubmitFunctions.map(async (func) => {
        const AsyncFunction = (async () => {}).constructor
        const GeneratorFunction = function* () {}.constructor
        //checks if the function is an async function
        if (
          (func instanceof AsyncFunction &&
            AsyncFunction !== Function &&
            AsyncFunction !== GeneratorFunction) === true
        ) {
          await func()
          return 1
        } else {
          func()
          return 1
        }
      }),
    )
  }

  const finalButtonColor = buttonColor ? buttonColor : action === 'submit' ? 'success' : 'error'
  return (
    <Dialog
      open={visibility}
      PaperComponent={ConfirmationModalDialogPaper}
      onClose={() => {
        handleVisibilitySetter(false)
      }}
    >
      <DialogTitle>
        <h4>
          <strong>Confirm {action}?</strong>
        </h4>
      </DialogTitle>
      {body !== '' && body !== undefined && (
        <DialogContent>
          <div className="d-flex flex-column">{body}</div>
        </DialogContent>
      )}
      <DialogActions>
        <ConfirmationModalSecondaryButton
          color="secondary"
          onClick={() => handleVisibilitySetter(false)}
        >
          Close
        </ConfirmationModalSecondaryButton>
        <ConfirmationModalPrimaryButton
          color={finalButtonColor}
          variant="contained"
          onClick={handleSubmit}
        >
          Confirm
        </ConfirmationModalPrimaryButton>
      </DialogActions>
    </Dialog>
  )
}

export function SentryRefHandler({ hasNextPage, loading, sentryRef, children }) {
  return (
    hasNextPage &&
    !loading && (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px 0px',
        }}
        className="__sentry_ref_handler"
        ref={sentryRef}
      >
        {children ? children : <CircularProgress />}
      </div>
    )
  )
}

export const AnimatedHamburgerIcon = ({ openNow, clickFunction }) => {
  return (
    <AnimatedHamburgerIconContainer onClick={clickFunction}>
      <AnimatedHamburgerIconHorizontalLine topLine openNow={openNow} />
      <AnimatedHamburgerIconHorizontalLine centerLine openNow={openNow} />
      <AnimatedHamburgerIconHorizontalLine bottomLine openNow={openNow} />
    </AnimatedHamburgerIconContainer>
  )
}

export function CustomReactPlayer({
  url,
  controls,
  defaultBackgroundImageUrl,
  mediaTitle,
  onFinish = () => {},
  containerStyles = {},
  playerControlsStyles = {},
  ...rest
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [loading, setLoading] = useState(true)
  const [played, setPlayed] = useState(0)
  const playerRef = useRef()
  const duration = playerRef.current ? playerRef.current.getDuration() : 0
  const [hasVideo, setHasVideo] = useState(false)
  const [subtitles, setSubtitles] = useState([])

  const sub = subtitleFile

  const [backgroundImageUrlToUse, setBackgroundImageUrlToUse] = useState()

  function handleSeek(e, newValue) {
    const value = newValue
    playerRef.current.seekTo(value)
    setPlayed(value)
  }

  function onClickFullscreen() {
    screenfull.request(playerRef.current)
  }

  async function detectMediaType() {
    try {
      const response = await fetch(url)
      const text = await response.text()
      const videoCodecs = ['avc1', 'hev1', 'hvc1', 'vp8', 'vp9', 'av01']
      const videoRegex = new RegExp(`CODECS="[^"]*(${videoCodecs.join('|')})`, 'i')
      const hasVideoStream = videoRegex.test(text)
      console.log({ hasVideoStream, text })
      setHasVideo(hasVideoStream)
    } catch (error) {
      console.error('Error fetching or parsing the manifest file:', error)
      setHasVideo(true)
    }
  }

  async function Setup() {
    var backgroundImgUrl = defaultBackgroundImageUrl
    if (!backgroundImgUrl) {
      backgroundImgUrl = await getImageLink(undefined, undefined)
    }
    setBackgroundImageUrlToUse(backgroundImgUrl)
    detectMediaType()
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0
      setPlayed(currentTime)
    }, 500)

    Setup()
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (isPlaying && duration !== 0 && duration !== undefined && duration !== null) {
      if (played === duration) {
        onFinish()
      }
    }
  }, [played])

  return (
    <CustomPlayerContainer style={containerStyles}>
      <StyledCustomReactPlayer
        ref={playerRef}
        playing={isPlaying}
        light={backgroundImageUrlToUse}
        style={{
          backgroundImage: `url(${backgroundImageUrlToUse})`,
          backgroundSize: 'cover',
        }}
        config={{
          file: {
            attributes: {
              crossOrigin: 'anonymous',
            },
            tracks: [
              {
                kind: 'subtitles',
                src: url.split('/').slice(0, -1).join('/') + '/subtitles_en.vtt?version=1.11',
                // src: subtitleFile,
                srcLang: 'en',
                default: true,
              },
            ],
          },
        }}
        url={url}
        height={'100%'}
        width={'100%'}
        onClickPreview={() => {
          setLoading(false)
          setIsPlaying(true)
        }}
        {...rest}
      />
      {!loading && (
        <>
          <CustomPlayerControlsContainer style={playerControlsStyles}>
            <IconButton
              sx={{ color: '#fff', p: '4px' }}
              onClick={() => setIsPlaying((prev) => !prev)}
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <CustomPlayerSliderContainer>
              {/* <PrettoSlider
            value={played}
            onChangeCommitted={handleSeekCommitted}
            marks={marks}
            onChange={handleSeek}
          /> */}
              <span className="duration-text">
                {Math.floor((played / 60) % 60)}:{String(Math.floor(played % 60)).padStart(2, '0')}
              </span>
              <StyledCustomReactPlayerSlider
                value={played}
                onChange={handleSeek}
                max={duration}
                size="small"
              />
              {/* <IconButton onClick={onClickFullscreen}>
                <Fullscreen />
              </IconButton> */}
            </CustomPlayerSliderContainer>
          </CustomPlayerControlsContainer>
        </>
      )}
    </CustomPlayerContainer>
  )
}

export function MuiInfinitySymbolIcon({ ...rest }) {
  return (
    <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" {...rest}>
      <path d="M18.6 6.62c-1.44 0-2.8.56-3.77 1.53L12 10.66 10.48 12h.01L7.8 14.39c-.64.64-1.49.99-2.4.99-1.87 0-3.39-1.51-3.39-3.38S3.53 8.62 5.4 8.62c.91 0 1.76.35 2.44 1.03l1.13 1 1.51-1.34L9.22 8.2C8.2 7.18 6.84 6.62 5.4 6.62 2.42 6.62 0 9.04 0 12s2.42 5.38 5.4 5.38c1.44 0 2.8-.56 3.77-1.53l2.83-2.5.01.01L13.52 12h-.01l2.69-2.39c.64-.64 1.49-.99 2.4-.99 1.87 0 3.39 1.51 3.39 3.38s-1.52 3.38-3.39 3.38c-.9 0-1.76-.35-2.44-1.03l-1.14-1.01-1.51 1.34 1.27 1.12c1.02 1.01 2.37 1.57 3.82 1.57 2.98 0 5.4-2.41 5.4-5.38s-2.42-5.37-5.4-5.37"></path>
    </svg>
  )
}

export function MuiCustomTabPanel(props) {
  const { children, tabValue, currentTab, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={tabValue !== currentTab}
      id={`simple-tabpanel-${currentTab}`}
      aria-labelledby={`simple-tab-${currentTab}`}
      {...other}
    >
      {tabValue === currentTab && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

// function addTracks() {
//   const videoElement = playerRef.current.getInternalPlayer()
//   if (videoElement) {
//     // Clear existing tracks
//     while (videoElement.firstChild) {
//       videoElement.removeChild(videoElement.firstChild)
//     }

//     videoElement.crossOrigin = 'anonymous'
//     // Add subtitle tracks
//     const enTrack = document.createElement('track')
//     enTrack.kind = 'subtitles'
//     enTrack.label = 'English'
//     enTrack.srclang = 'en'
//     // enTrack.src = `
//     //   https://hls.aumhum.xyz/prod/MEDIA-da6015b7fa4e443f9af292911c3e5774/1716462466653/`

//     const trackSrc = url.split('/').slice(0, -1).join('/') + '/subtitles_en.vtt?version=1.11'
//     enTrack.src = trackSrc
//     enTrack.default = true
//     videoElement.appendChild(enTrack)

//     // const esTrack = document.createElement('track')
//     // esTrack.kind = 'subtitles'
//     // esTrack.label = 'Spanish'
//     // esTrack.srclang = 'es'
//     // esTrack.src =
//     //   'https://your-cloudfront-distribution-domain/hls/prod/subtitles_es.vtt?Expires=timestamp&Signature=signature&Key-Pair-Id=key-pair-id'
//     // videoElement.appendChild(esTrack)
//   }
// }

// useEffect(() => {
//   if (playerRef.current.getInternalPlayer()) {
//     // Wait for the video element to be ready
//     playerRef.current.getInternalPlayer().addEventListener('loadedmetadata', addTracks)
//   }
//   return () => {
//     if (playerRef.current.getInternalPlayer()) {
//       playerRef.current.getInternalPlayer().removeEventListener('loadedmetadata', addTracks)
//     }
//   }
// }, [url, playerRef, isPlaying])

// useEffect(() => {
//   const fetchSubtitles = async () => {
//     try {
//       const response = await fetch(url)
//       const text = await response.text()

//       const subtitleTracks = []
//       const regex = /#EXT-X-MEDIA:TYPE=SUBTITLES,.*?(?:URI="(.*?)")/g
//       let match
//       while ((match = regex.exec(text)) !== null) {
//         const subtitleUrl = match[1]
//         const labelMatch = /NAME="(.*?)"/.exec(match[0])
//         const langMatch = /LANGUAGE="(.*?)"/.exec(match[0])

//         if (subtitleUrl && labelMatch && langMatch) {
//           subtitleTracks.push({
//             src: subtitleUrl,
//             label: labelMatch[1],
//             srclang: langMatch[1],
//             default: match[0].includes('DEFAULT=YES'),
//           })
//         }
//       }

//       setSubtitles(subtitleTracks)
//     } catch (error) {
//       console.error('Error fetching HLS manifest:', error)
//     }
//   }

//   fetchSubtitles()
// }, [url])

// useEffect(() => {
//   const videoElement = playerRef.current.getInternalPlayer()
//   if (videoElement) {
//     subtitles.forEach((sub) => {
//       const track = document.createElement('track')
//       track.kind = 'subtitles'
//       track.label = sub.label
//       track.srclang = sub.srclang
//       track.src = sub.src
//       track.default = sub.default
//       videoElement.appendChild(track)
//     })
//   }
// }, [subtitles])

export const GenericLoadingOverlay = ({ backgroundColor, spinnerVariant }) => {
  return (
    <GenericLoadingOverlayBase backgroundColor={backgroundColor}>
      <Spinner animation="border" variant={spinnerVariant || 'dark'} size="md" />
    </GenericLoadingOverlayBase>
  )
}

export function GridSectionWrapper({
  headerComponents,
  heading,
  subheading,
  shadow = true,
  children,
  sectionContainerSx = {},
  sectionContainerProps = {},
}) {
  return (
    <SectionGridItem
      xs={12}
      {...sectionContainerProps}
      sx={sectionContainerSx}
      item
      shadow={shadow}
    >
      <Grid container spacing={2}>
        {heading && !headerComponents ? (
          <Grid item xs={12}>
            <h5>{heading}</h5>
          </Grid>
        ) : (
          headerComponents && (
            <Grid item xs={12}>
              {headerComponents}
            </Grid>
          )
        )}
        {children}
      </Grid>
    </SectionGridItem>
  )
}

export const AutocompleteHelperButton = ({ text, Icon, iconProps, clickFunction }) => {
  return (
    <AutocompleteHelperStyledButton onClick={clickFunction}>
      <span style={{ marginRight: '8px' }}>{text}</span>
      <Icon {...iconProps} />
    </AutocompleteHelperStyledButton>
  )
}

const generateTimeOptions = (valueFormat = 'HH:mm', labelFormat = 'HH:mm') => {
  const options = []
  let time = dayjs().startOf('day')

  while (time.isBefore(dayjs().endOf('day'))) {
    options.push({ value: time.format(valueFormat), label: time.format(labelFormat) })
    time = time.add(15, 'minute')
  }

  return options
}

export const TimeRangePicker = ({
  label,
  value,
  onChange,
  valueFormat = 'HH:mm',
  labelFormat = 'HH:mm',
  timeOptions: customTimeOptions = null, // Custom time options (if provided, overrides default generation)
  toTimeOptions: customToTimeOptions = null, // Custom "to" time options (if provided, used for end time dropdown)
  validateCustomTime = null, // Optional function to validate if a custom time is allowed
}) => {
  const [errors, setErrors] = useState({ start: '', end: '' })
  const [inputValues, setInputValues] = useState({ start: '', end: '' })
  const timeOptions = useMemo(
    () => customTimeOptions || generateTimeOptions(valueFormat, labelFormat),
    [customTimeOptions, valueFormat, labelFormat],
  )
  
  // Use custom "to" time options if provided, otherwise use same as start
  const toTimeOptions = useMemo(
    () => customToTimeOptions || timeOptions,
    [customToTimeOptions, timeOptions],
  )

  const validateTime = (time, field) => {
    if (!time) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
      return false
    }
    if (!dayjs(time, valueFormat, true).isValid()) {
      setErrors((prev) => ({ ...prev, [field]: 'Invalid time format' }))
      return false
    }
    
    // If custom validation function is provided, use it
    if (validateCustomTime) {
      const isValid = validateCustomTime(time, field)
      if (!isValid) {
        setErrors((prev) => ({ ...prev, [field]: 'Time not within valid schedule' }))
        return false
      }
    }
    
    setErrors((prev) => ({ ...prev, [field]: '' }))
    return true
  }
  
  // Check if a custom time input is valid (for isValidNewOption)
  const isValidCustomTime = (inputValue) => {
    if (!inputValue) return false
    
    // Check if it's a valid time format
    if (!dayjs(inputValue, valueFormat, true).isValid()) {
      return false
    }
    
    // If custom validation function is provided, use it
    if (validateCustomTime) {
      return validateCustomTime(inputValue, 'start') // Use 'start' as default for validation
    }
    
    return true
  }

  const handleTimeChange = (inputValue, field) => {
    // Extract the actual time value from the input
    let newTime = inputValue
    if (typeof inputValue === 'object' && inputValue !== null) {
      newTime = inputValue.value || inputValue.inputValue || inputValue
    }
    
    if (!newTime || newTime === '') {
      return
    }
    
    if (validateTime(newTime, field)) {
      const newValue = field === 'start' ? [newTime, value[1]] : [value[0], newTime]

      // Check if end time is still valid after start time change
      if (
        newValue[0] &&
        newValue[1] &&
        dayjs(newValue[0], valueFormat).isAfter(
          dayjs(newValue[1], valueFormat).subtract(1, 'second'),
        )
      ) {
        // If start is after end, reset end time to next slot after start
        if (field === 'start') {
          const nextSlot = dayjs(newValue[0], valueFormat).add(15, 'minute').format(valueFormat)
          newValue[1] = nextSlot
        } else {
          // If end is before start, don't update
          return
        }
      }
      
      setErrors({ start: '', end: '' })
      onChange(newValue)
    }
  }

  return (
    <div>
      {label && <Typography>{label}</Typography>}
      <div className="d-flex align-items-center gap-1" style={{ flexWrap: 'nowrap' }}>
        {/* Start Time */}
        <CreatableSelect
          options={timeOptions}
          value={
            value[0] ? (timeOptions.find((option) => option.value === value[0]) || {
              value: value[0],
              label: value[0],
            }) : null
          }
          onChange={(selected, action) => {
            if (selected) {
              const selectedValue = selected.value || selected.inputValue || (typeof selected === 'string' ? selected : null)
              if (selectedValue) {
                handleTimeChange(selectedValue, 'start')
              }
            } else {
              // Handle clear
              handleTimeChange('', 'start')
            }
          }}
          onCreateOption={(inputValue) => {
            // Allow creating custom option if it's a valid time
            if (isValidCustomTime(inputValue)) {
              handleTimeChange(inputValue, 'start')
            }
          }}
          inputValue={inputValues.start}
          onInputChange={(inputValue, actionMeta) => {
            // Track input value for display
            setInputValues(prev => ({ ...prev, start: inputValue || '' }))
            
            // If user blurs, accept the typed value if it's valid
            if (actionMeta?.action === 'input-blur') {
              if (inputValue && isValidCustomTime(inputValue)) {
                handleTimeChange(inputValue, 'start')
                setInputValues(prev => ({ ...prev, start: '' }))
              }
            }
          }}
          onKeyDown={(e) => {
            // Handle Enter key to accept typed value
            if (e.key === 'Enter' && inputValues.start) {
              if (isValidCustomTime(inputValues.start)) {
                e.preventDefault()
                handleTimeChange(inputValues.start, 'start')
                setInputValues(prev => ({ ...prev, start: '' }))
              }
            }
          }}
          placeholder="Start Time"
          isValidNewOption={isValidCustomTime}
          formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
          components={{ DropdownIndicator: () => null }}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.start ? 'red' : base.borderColor,
              width: '80px',
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />

        {/* Divider */}
        <Typography sx={{ mx: 0.5, flexShrink: 0 }}>-</Typography>

        {/* End Time */}
        <CreatableSelect
          options={toTimeOptions}
          value={
            value[1] ? (toTimeOptions.find((option) => option.value === value[1]) || {
              value: value[1],
              label: value[1],
            }) : null
          }
          onChange={(selected, action) => {
            if (selected) {
              const selectedValue = selected.value || selected.inputValue || (typeof selected === 'string' ? selected : null)
              if (selectedValue) {
                handleTimeChange(selectedValue, 'end')
              }
            } else {
              // Handle clear
              handleTimeChange('', 'end')
            }
          }}
          onCreateOption={(inputValue) => {
            // Allow creating custom option if it's a valid time
            if (isValidCustomTime(inputValue)) {
              handleTimeChange(inputValue, 'end')
            }
          }}
          inputValue={inputValues.end}
          onInputChange={(inputValue, actionMeta) => {
            // Track input value for display
            setInputValues(prev => ({ ...prev, end: inputValue || '' }))
            
            // If user blurs, accept the typed value if it's valid
            if (actionMeta?.action === 'input-blur') {
              if (inputValue && isValidCustomTime(inputValue)) {
                handleTimeChange(inputValue, 'end')
                setInputValues(prev => ({ ...prev, end: '' }))
              }
            }
          }}
          onKeyDown={(e) => {
            // Handle Enter key to accept typed value
            if (e.key === 'Enter' && inputValues.end) {
              if (isValidCustomTime(inputValues.end)) {
                e.preventDefault()
                handleTimeChange(inputValues.end, 'end')
                setInputValues(prev => ({ ...prev, end: '' }))
              }
            }
          }}
          placeholder="End Time"
          isValidNewOption={isValidCustomTime}
          formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
          components={{ DropdownIndicator: () => null }}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.end ? 'red' : base.borderColor,
              width: '80px',
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>
    </div>
  )
}
